import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Message, Agent } from '../types';
import conversationHistoryService, { ConversationSummary, ConversationHistory } from '../services/conversationHistoryService';
import { useAuth } from './useAuth';

export type RefreshConversationsReason = 'initial' | 'message' | 'manual';

export interface RefreshConversationsOptions {
  reason?: RefreshConversationsReason;
  force?: boolean;
}

export interface UseChatHistoryReturn {
  // Conversation state
  conversations: ConversationSummary[];
  currentConversationId: string | null;
  isLoading: boolean;
  error: string | null;
  
  // Core operations
  saveConversation: (messages: Message[], agent: Agent) => Promise<string | null>;
  loadConversation: (conversationId: string) => Promise<Message[] | null>;
  loadConversationFull: (conversationId: string) => Promise<ConversationHistory | null>;
  deleteConversation: (conversationId: string) => Promise<boolean>;
  clearAllConversations: () => Promise<boolean>;
  updateConversation: (conversationId: string, messages: Message[], agent: Agent) => Promise<boolean>;
  
  // Chat management
  startNewConversation: () => void;
  refreshConversations: (options?: RefreshConversationsOptions) => Promise<void>;
  
  // Utilities
  clearError: () => void;
  getConversationTitle: (conversationId: string) => string | null;
  setActiveConversation: (conversationId: string | null) => void;
}


const CURRENT_CONVERSATION_KEY = 'unichat_current_conversation';

export const useChatHistory = (): UseChatHistoryReturn => {
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [hasExplicitlyStartedNew, setHasExplicitlyStartedNew] = useState(false);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  
  // Add save lock to prevent race conditions
  const [isSaving, setIsSaving] = useState<boolean>(false);
  
  const { getUserEmail, isAuthenticated, isLoading: authLoading, user } = useAuth();

  // Keep track of initialization attempts
  const initAttempts = React.useRef(0);
  const maxInitAttempts = 5;
  
  const hasLoadedInitialConversationsRef = useRef(false);
  const refreshInFlightRef = useRef(false);
  const lastRefreshReasonRef = useRef<'initial' | 'message' | 'manual' | null>(null);
  const lastRefreshTimeRef = useRef<number>(0);

  // Initialize the service and load preferences
  useEffect(() => {
    const initializeService = async () => {
      try {
        // Check for global auth state refresh flag
        const globalAuthRefreshed = window.authStateRefreshed;
        const globalAuthTimestamp = window.authStateRefreshTimestamp || 0;
        const lastCheckTime = Date.now() - 5000; // Consider events in last 5 seconds
        
        // Track initialization attempts
        initAttempts.current += 1;
        
        // console.log('🔍 useChatHistory: Checking initialization conditions...', {
        //   authLoading,
        //   isAuthenticated,
        //   hasUser: !!user,
        //   userDetails: user?.userDetails,
        //   globalAuthRefreshed,
        //   globalAuthTimestamp: globalAuthTimestamp ? new Date(globalAuthTimestamp).toISOString() : 'none',
        //   recentAuthChange: globalAuthTimestamp > lastCheckTime
        // });
        
        // If we have a recent global auth state refresh, trust that over our local state
        if (globalAuthRefreshed && globalAuthTimestamp > lastCheckTime) {
          // console.log('✅ useChatHistory: Recent auth state change detected, proceeding with initialization');
          // Continue with initialization, but still check for user object
        } else {
          // Wait for authentication to complete
          if (authLoading) {
            console.log('useChatHistory: Authentication still loading, waiting...');
            return;
          }
          
          if (!isAuthenticated) {
            console.log('useChatHistory: User not authenticated, skipping initialization');
            return;
          }
        }
        
        // Always ensure we have a user object before proceeding
        // This is critical because the backend needs a valid user ID
        
        // Ensure we have a complete user object
        if (!user || !user.userDetails) {
          console.log('useChatHistory: User object not fully loaded yet, waiting...', { 
            hasUser: !!user, 
            userDetails: user?.userDetails,
            attempt: initAttempts.current,
            maxAttempts: maxInitAttempts
          });
          
          // If we've tried several times and still don't have user, try to force refresh
          if (initAttempts.current >= maxInitAttempts) {
            console.log('⚠️ useChatHistory: Max init attempts reached, trying to force refresh auth state...');
            
            // Try to get a fresh user object directly from auth service
            try {
              const authService = await import('../services/auth').then(m => m.default);
              const instance = authService.getInstance();
              const freshUser = await instance.getCurrentUser();
              
              if (freshUser && freshUser.userDetails) {
                console.log('✅ useChatHistory: Successfully retrieved fresh user:', freshUser.userDetails);
                
                // Get email directly from fresh user instead of using stale state
                const directEmail = instance.getUserEmail(freshUser);
                console.log('useChatHistory: Got direct user email:', directEmail);
                
                if (directEmail && directEmail.trim() !== '') {
                  console.log('✅ useChatHistory: Proceeding with direct email:', directEmail);
                  
                  // Initialize with direct email and complete the initialization
                  await conversationHistoryService.initialize(directEmail);
                  console.log('✅ useChatHistory: Service initialized with direct email');
                  setIsInitialized(true);
                  
                  // Load initial conversations
                  console.log('Loading initial conversation history...');
                  await refreshConversations({ reason: 'initial' });
                  console.log('Initial conversations loaded:', conversations.length, 'conversations');
                  
                  // Reset attempt counter after success
                  initAttempts.current = 0;
                  return; // Successfully initialized, exit early
                } else {
                  console.warn('❌ useChatHistory: No email available from fresh user');
                  return;
                }
              } else {
                console.log('❌ useChatHistory: Still no user after force refresh');
                return;
              }
            } catch (error) {
              console.error('❌ useChatHistory: Error force refreshing user:', error);
              return;
            }
          } else {
            return;
          }
        }
        
        // Only reach this point if we have a user from the normal flow
        const userEmail = getUserEmail();
        if (!userEmail || userEmail.trim() === '') {
          console.warn('No user email available for conversation history - will retry when auth completes');
          return;
        }

        // Initialize the conversation history service
        await conversationHistoryService.initialize(userEmail);
        
        // Load current conversation ID only if user hasn't explicitly started a new conversation
        const savedConversationId = localStorage.getItem(CURRENT_CONVERSATION_KEY);
        
        if (savedConversationId && savedConversationId !== 'null' && !hasExplicitlyStartedNew) {
          setCurrentConversationId(savedConversationId);
        } else {
          // Ensure we're truly starting fresh
          setCurrentConversationId(null);
        }
        
        setIsInitialized(true);
        
        // Load conversations after initialization
        setIsLoading(true);
        try {
          await refreshConversations({ reason: 'initial' });
        } catch (error) {
          console.error('Failed to load initial conversations:', error);
          setError('Failed to load conversation history');
        } finally {
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Failed to initialize conversation history service:', error);
        setError('Failed to initialize conversation history');
      }
    };

    initializeService();
    
    // Add event listener for auth state changes
    const handleAuthStateChanged = (event: Event) => {
      const customEvent = event as CustomEvent<{authenticated: boolean, timestamp: number}>;
      console.log('🔔 useChatHistory: Auth state change event received', customEvent.detail);
      if (customEvent.detail?.authenticated) {
        // Reset attempt counter for new auth events
        initAttempts.current = 0;
        
        // Try initialization immediately
        initializeService();
        
        // Also try again after a short delay to ensure user object is loaded
        setTimeout(() => {
          // console.log('🔄 useChatHistory: Delayed retry after auth event');
          initializeService();
        }, 500);
      }
    };
    
    window.addEventListener('authStateChanged', handleAuthStateChanged as EventListener);
    
    return () => {
      window.removeEventListener('authStateChanged', handleAuthStateChanged as EventListener);
    };
  }, [getUserEmail, isAuthenticated, authLoading, user, hasExplicitlyStartedNew]);

  // Save current conversation ID to localStorage
  useEffect(() => {
    if (currentConversationId) {
      localStorage.setItem(CURRENT_CONVERSATION_KEY, currentConversationId);
    } else {
      localStorage.removeItem(CURRENT_CONVERSATION_KEY);
    }
  }, [currentConversationId]);

  // Refresh conversations list
  interface RefreshOptions {
    reason?: 'initial' | 'message' | 'manual';
    force?: boolean;
  }

  const refreshConversations = useCallback(async (options?: RefreshConversationsOptions) => {
    const { reason = 'manual', force = false } = options || {};
    if (!conversationHistoryService.isInitialized()) {
      console.warn('Conversation history service not initialized');
      return;
    }

    if (refreshInFlightRef.current) {
      return;
    }

    if (!force) {
      if (reason === 'initial' && hasLoadedInitialConversationsRef.current) {
        return;
      }

      const now = Date.now();
      const lastReason = lastRefreshReasonRef.current;
      const lastTime = lastRefreshTimeRef.current;
      const isRecentMessageRefresh = reason === 'message' && lastReason === 'message' && (now - lastTime) < 2000;

      if (isRecentMessageRefresh) {
        return;
      }
    }

    refreshInFlightRef.current = true;
    try {
      if (reason === 'manual') {
        setIsLoading(true);
      }
      setError(null);
      // Preload the first 10 conversations with messages for instant loading
      const conversationList = await conversationHistoryService.listConversations({
        includeMessages: true,
        limit: 10
      });
      setConversations(conversationList);
      if (reason === 'initial') {
        hasLoadedInitialConversationsRef.current = true;
      }
      lastRefreshReasonRef.current = reason;
      lastRefreshTimeRef.current = Date.now();
    } catch (error) {
      console.error('Failed to load conversations:', error);
      setError('Failed to load conversation history');
    } finally {
      if (reason === 'manual') {
        setIsLoading(false);
      }
      refreshInFlightRef.current = false;
    }
  }, []);

  // Save conversation with improved race condition handling
  const saveConversation = useCallback(async (
    messages: Message[], 
    agent: Agent
  ): Promise<string | null> => {
    if (!isInitialized) {
      console.warn('Conversation history service not initialized');
      return null;
    }

    // Filter out empty messages, system messages, and incomplete messages for saving
    const messagesToSave = messages.filter(msg => 
      msg.content.trim().length > 0 && 
      msg.sender !== 'system' &&
      !msg.isStatusMessage &&
      (msg.isCompleted === undefined || msg.isCompleted === true)  // Include user messages (undefined) and completed agent messages (true)
    );

    if (messagesToSave.length === 0) {
      console.warn('No valid messages to save');
      return null;
    }

    try {
      setError(null);
      
      // Prevent multiple simultaneous saves
      if (isSaving) {
        return currentConversationId;
      }
      
      setIsSaving(true);
      
      // Improved logic to prevent race conditions
      // Always create new conversation if explicitly started new OR if no current conversation
      // Otherwise update existing conversation
      const shouldCreateNew = hasExplicitlyStartedNew || !currentConversationId;
      const conversationId = shouldCreateNew ? null : currentConversationId;
      
      const conversation = await conversationHistoryService.saveConversation(
        messagesToSave,
        agent,
        conversationId || undefined
      );
      
      const summary: ConversationSummary = {
        id: conversation.id,
        title: conversation.title,
        agentId: conversation.agentId,
        agentName: conversation.agentName,
        createdAt: conversation.createdAt,
        updatedAt: conversation.updatedAt,
        messageCount: conversation.messageCount,
        lastUserMessage: (conversation as any).lastUserMessage,
        lastAgentMessage: (conversation as any).lastAgentMessage
      };

      setConversations(prev => {
        const others = prev.filter(c => c.id !== summary.id);
        const updated = [summary, ...others];
        return updated.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      });

      if (!conversationId) {
        setCurrentConversationId(conversation.id);
        setHasExplicitlyStartedNew(false);
        localStorage.setItem(CURRENT_CONVERSATION_KEY, conversation.id);
      }
      
      return conversation.id;
    } catch (error) {
      console.error('Failed to save conversation:', error);
      setError('Failed to save conversation');
      return null;
    } finally {
      // Always release the save lock
      setIsSaving(false);
    }
  }, [isInitialized, currentConversationId, refreshConversations, hasExplicitlyStartedNew, isSaving]); // authLoading, isAuthenticated, getUserEmail, and user are not used in the callback

  // Load conversation for viewing (does NOT automatically set as active)
  const loadConversation = useCallback(async (conversationId: string): Promise<Message[] | null> => {
    if (!isInitialized) {
      console.warn('Conversation history service not initialized');
      return null;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      // Check if conversation is already preloaded in the conversations list
      const preloadedConv = conversations.find(c => c.id === conversationId);
      if (preloadedConv?.messages) {
        console.log(`✨ Loading conversation ${conversationId} from preloaded cache`);
        setIsLoading(false);
        return preloadedConv.messages;
      }
      
      // Not preloaded, fetch from backend
      console.log(`📥 Fetching conversation ${conversationId} from backend`);
      const conversation = await conversationHistoryService.getConversation(conversationId);
      if (!conversation) {
        setError('Conversation not found');
        return null;
      }
      
      return conversation.messages;
    } catch (error) {
      console.error('Failed to load conversation:', error);
      setError('Failed to load conversation');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [isInitialized, conversations]);

  // Load full conversation (messages + metadata) for viewing
  const loadConversationFull = useCallback(async (conversationId: string): Promise<ConversationHistory | null> => {
    if (!isInitialized) {
      console.warn('Conversation history service not initialized');
      return null;
    }

    try {
      setIsLoading(true);
      setError(null);
      const conversation = await conversationHistoryService.getConversation(conversationId);
      if (!conversation) {
        setError('Conversation not found');
        return null;
      }
      return conversation;
    } catch (error) {
      console.error('Failed to load conversation:', error);
      setError('Failed to load conversation');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [isInitialized]);

  // Delete conversation
  const deleteConversation = useCallback(async (conversationId: string): Promise<boolean> => {
    if (!isInitialized) {
      console.warn('Conversation history service not initialized');
      return false;
    }

    try {
      setError(null);
      const success = await conversationHistoryService.deleteConversation(conversationId);
      
      if (success) {
        if (currentConversationId === conversationId) {
          setCurrentConversationId(null);
        }
        setConversations(prev => prev.filter(c => c.id !== conversationId));
      }

      return success;
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      setError('Failed to delete conversation');
      return false;
    }
  }, [isInitialized, currentConversationId, refreshConversations]);

  // Clear all conversations
  const clearAllConversations = useCallback(async (): Promise<boolean> => {
    if (!isInitialized) {
      console.warn('Conversation history service not initialized');
      return false;
    }

    try {
      setError(null);
      const success = await conversationHistoryService.clearAllConversations();
      
      if (success) {
        setCurrentConversationId(null);
        setConversations([]);
      }

      return success;
    } catch (error) {
      console.error('Failed to clear all conversations:', error);
      setError('Failed to clear conversations');
      return false;
    }
  }, [isInitialized, refreshConversations]);

  // Update existing conversation
  const updateConversation = useCallback(async (
    conversationId: string, 
    messages: Message[], 
    agent: Agent
  ): Promise<boolean> => {
    if (!isInitialized) {
      console.warn('Conversation history service not initialized');
      return false;
    }

    try {
      setError(null);
      
      // Filter out empty messages, system messages, and incomplete messages for saving
      const messagesToSave = messages.filter(msg => 
        msg.content.trim().length > 0 && 
        msg.sender !== 'system' &&
        !msg.isStatusMessage &&
        (msg.isCompleted === undefined || msg.isCompleted === true)  // Include user messages (undefined) and completed agent messages (true)
      );

      const updatedConversation = await conversationHistoryService.updateConversation(
        conversationId,
        messagesToSave,
        agent
      );
      
      if (updatedConversation) {
        const summary: ConversationSummary = {
          id: updatedConversation.id,
          title: updatedConversation.title,
          agentId: updatedConversation.agentId,
          agentName: updatedConversation.agentName,
          createdAt: updatedConversation.createdAt,
          updatedAt: updatedConversation.updatedAt,
          messageCount: updatedConversation.messageCount,
          lastUserMessage: (updatedConversation as any).lastUserMessage,
          lastAgentMessage: (updatedConversation as any).lastAgentMessage
        };

        setConversations(prev => {
          const others = prev.filter(c => c.id !== summary.id);
          const updated = [summary, ...others];
          return updated.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        });
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Failed to update conversation:', error);
      setError('Failed to update conversation');
      return false;
    }
  }, [isInitialized, refreshConversations, setError]);

  // Start new conversation with improved state management
  const startNewConversation = useCallback(() => {
    // Clear localStorage FIRST to prevent race conditions
    localStorage.removeItem(CURRENT_CONVERSATION_KEY);
    
    // Set states in correct order to prevent race conditions
    setHasExplicitlyStartedNew(true);
    setCurrentConversationId(null);
    setError(null);
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Set active conversation (for when clicking on conversation history)
  const setActiveConversation = useCallback((conversationId: string | null) => {
    setCurrentConversationId(conversationId);
  }, []);

  // Get conversation title by ID
  const getConversationTitle = useCallback((conversationId: string): string | null => {
    const conversation = conversations.find(c => c.id === conversationId);
    return conversation?.title || null;
  }, [conversations]);

  return {
    // State
    conversations,
    currentConversationId,
    isLoading,
    error,
    
    // Operations
    saveConversation,
    loadConversation,
    loadConversationFull,
    deleteConversation,
    clearAllConversations,
    updateConversation,
    
    // Management
    startNewConversation,
    refreshConversations,
    setActiveConversation,
    
    // Utilities
    clearError,
    getConversationTitle
  };
};